import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import type { ImageRef, SceneRef } from '../../../shared/models';
import {
	NO_OCCUPIED_SLUGS,
	takenImageSlugs,
	takenSceneSlugs,
	type OccupiedSlugs,
} from '../utils/taken-slugs';

/**
 * Which scene/image slugs the DATABASE holds, so a create can avoid them
 * (DEV-670).
 *
 * The editor only ever sees `project.json`'s reference graph. A document no
 * ref points at is invisible there but still owns its row on the unique
 * `(projectSlug, chapterSlug, sceneSlug)` key, so a new entity that lands on
 * its slug replaces its content outright. This service holds the missing half
 * of the picture.
 *
 * Fetched **once** alongside the project, not per create — the map is per
 * project and tiny, and the round trip would otherwise sit in front of every
 * add. Staleness is asymmetric and deliberately so:
 *
 * - A create never invalidates it: the new slug immediately becomes a ref, and
 *   refs are the other half of the taken set.
 * - A delete frees a slug, and a map that still lists it would renumber a
 *   re-add for no reason (`intro` → `intro-2` right after deleting `intro`).
 *   `forget*` drops the entry locally instead of re-fetching.
 * - Another tab or session can still occupy a slug behind our back. That
 *   window is closed by the backend, which refuses a create-flagged write onto
 *   an occupied key rather than upserting over it.
 */
@Injectable()
export class EditorSlugsService {
	private http = inject(HttpClient);

	private readonly occupied = signal<OccupiedSlugs>(NO_OCCUPIED_SLUGS);

	/** Load the map for a project. Failure is non-fatal — we fall back to refs-only. */
	load(projectSlug: string): void {
		this.http
			.get<OccupiedSlugs>(`${environment.apiUrl}/api/projects/${projectSlug}/occupied-slugs`)
			.subscribe({
				next: (map) => this.occupied.set({ scenes: map.scenes ?? {}, images: map.images ?? {} }),
				error: () => this.occupied.set(NO_OCCUPIED_SLUGS),
			});
	}

	/**
	 * Slugs a new scene in this container must avoid: the caller's own refs ∪
	 * the documents. `refs` is the caller's draft, not the published project —
	 * a form patches its clone before the parent has merged it back.
	 */
	takenSceneSlugs(refs: readonly Pick<SceneRef, 'slug' | 'split'>[], chapterSlug: string | null): Set<string> {
		return takenSceneSlugs(refs, chapterSlug, this.occupied());
	}

	/** Slugs a new image in this container must avoid: the caller's own refs ∪ the documents. */
	takenImageSlugs(refs: readonly Pick<ImageRef, 'slug'>[], chapterSlug: string | null): Set<string> {
		return takenImageSlugs(refs, chapterSlug, this.occupied());
	}

	/**
	 * A deleted document frees its slug — drop it so an immediate re-add keeps
	 * the name. Exact match only: a split family's children (`slug__i`) are not
	 * assumed gone, because over-reporting a slug as free is the one error that
	 * re-opens the overwrite, while over-reporting it as taken only renumbers.
	 */
	forgetScene(chapterSlug: string | null, sceneSlug: string): void {
		this.forget('scenes', chapterSlug, (slug) => slug !== sceneSlug);
	}

	forgetImage(chapterSlug: string | null, imageSlug: string): void {
		this.forget('images', chapterSlug, (slug) => slug !== imageSlug);
	}

	/** A chapter delete cascades to every scene and image document under it. */
	forgetChapter(chapterSlug: string): void {
		this.occupied.update((current) => {
			const scenes = { ...current.scenes };
			const images = { ...current.images };
			delete scenes[chapterSlug];
			delete images[chapterSlug];
			return { scenes, images };
		});
	}

	private forget(kind: 'scenes' | 'images', chapterSlug: string | null, keep: (slug: string) => boolean): void {
		const key = chapterSlug ?? '';
		this.occupied.update((current) => {
			const bucket = current[kind][key];
			if (!bucket) return current;
			return { ...current, [kind]: { ...current[kind], [key]: bucket.filter(keep) } };
		});
	}
}
